import axios, { AxiosInstance } from 'axios';
import { parse } from 'qs';

import { ContentfulClientApi, createClient } from 'contentful';
import { ContentfulClientConfig } from '../api/contentful/ContentfulClientConfig';
import { readCookie } from './Cookies';

// declare the regexes once
const DEV = /^(stg|dev|sandbox|qa)/;
const DEV_HOST = /.*(firebaseapp\.com|ngrok\.io|netlify\.app)/;
export const LOCALHOST = /^(local|localhost|192|10)/;
const DEV_ENV = /dev.+\.dev\.clover\.com$/;
const STAGING_ENV = /stg.+\.dev\.clover\.com$/;
const CLOVER_HOST = /^(.*\.)?clover\.com$/;

// Exported for testing
export const API_LOCAL_URL = 'https://apidev1.dev.clover.com';
export const API_DEV_URL = 'https://apidev1.dev.clover.com';
export const API_STG_URL = 'https://apistg1.dev.clover.com';
export const API_PROD_URL = 'https://api.clover.com';

export class Environment {

  /**
   * Is a Dev based location (stg1, dev1, firebase, etc)
   */
  get isDev() {
    // tslint:disable-next-line:max-line-length
    const isDev = DEV.test(this.context.location.hostname.split('.')[0]);
    return (this.isLocalhost || isDev || this.isDevHost) && !Environment.useProd();
  }

  /**
   * Is Localhost based on hostname
   */
  get isLocalhost() {
    return LOCALHOST.test(this.context.location.hostname.split('.')[0]);
  }

  /**
   * Is a non-Clover dev host (firebase, netlify, etc)
   */
  get isDevHost() {
    return DEV_HOST.test(this.context.location.hostname);
  }

  /**
   * Is a Clover dev environment (devX.dev.clover.com)
   */
  get isCloverDevEnvironment() {
    return DEV_ENV.test(this.context.location.hostname);
  }

  /**
   * Is a Clover stage environment (stgX.dev.clover.com)
   */
  get isCloverStagingEnvironment() {
    return STAGING_ENV.test(this.context.location.hostname);
  }

  /**
   * Is a Clover prod environment (www.clover.com)
   */
  get isCloverProdEnvironment() {
    return this.isCloverHost && !this.isDev && !this.isLocalhost;
  }

  get isCloverHost() {
    return CLOVER_HOST.test(this.context.location.hostname);
  }

  get hostname() {
    return this.context.location.hostname;
  }

  get isExistingMerchant() {
    return readCookie('isCloverMerchant') === 'true';
  }

  /**
   * @deprecated Use {@link appEnvironmentName} instead of {@link environmentFriendlyName}
   */
  get environmentFriendlyName() {
    if (this.isCloverStagingEnvironment) {
      return 'staging';
    } else if (this.isCloverProdEnvironment) {
      return 'production';
    } else if (this.isCloverDevEnvironment) {
      return 'qa';
    } else {
      return 'development';
    }
  }
  /**
   * return the environment friendly name
   */
  get appEnvironmentName() {
    if (this.isCloverStagingEnvironment) {
      return 'stg';
    } else if (this.isCloverProdEnvironment) {
      return 'usprod';
    } else if (this.isCloverDevEnvironment) {
      return 'dev';
    } else {
      return 'local';
    }
  }

  get environmentForTealium() {
    if (this.isDevHost || this.isCloverDevEnvironment) {
      return 'dev';
    } else if (this.isCloverStagingEnvironment) {
      return 'qa';
    } else if (this.isCloverProdEnvironment) {
      return 'prod';
    } else {
      return '';
    }
  }

  get cosApiClient(): AxiosInstance {
    return this.cosApiClientInstance || (this.cosApiClientInstance = axios.create({
      baseURL: '/',
    }));
  }

  get growthApiClientBaseUrl() {
    if (this.isLocalhost) {
      return API_LOCAL_URL;
    }

    switch (this.environmentFriendlyName) {
      case 'production':
        return API_PROD_URL;
      case 'staging':
        return API_STG_URL;
      case 'qa':
      case 'development':
        return API_DEV_URL;
      default:
        return '';
    }
  }

  get dcsApiClientBaseUrl() {
    if (this.environmentFriendlyName === 'production' || !Environment.usePreview()) {
      return API_PROD_URL;
    } else {
      return API_STG_URL;
    }
  }

  get growthApiClient(): AxiosInstance {
    return this.growthApiClientInstance || (this.growthApiClientInstance = axios.create({
      baseURL: this.growthApiClientBaseUrl,
    }));
  }

  get dcsApiClient(): AxiosInstance {
    return this.dcsApiClientInstance || (this.dcsApiClientInstance = axios.create({
      baseURL: `${this.dcsApiClientBaseUrl}/digitalcatalog`,
    }));
  }

  get bankPersonalizationJwtPublicKey(): string {
    return this.isCloverProdEnvironment ? Environment.PROD_BANK_PERSONALIZATION_JWT_PUBLIC_KEY : Environment.DEV_BANK_PERSONALIZATION_JWT_PUBLIC_KEY;
  }

  get shopContentApi(): ContentfulClientApi {
    return createClient(Environment.getShopContentfulConfig());
  }

  static current = new Environment(window);

  // ga properties exposed for tests
  static readonly DEV_GA_PROPERTY_ID = 'UA-78671710-13';
  static readonly PROD_GA_PROPERTY_ID = 'UA-22630262-25';

  static readonly GOOGLE_RECAPTCHA_SITE_KEY = '6LegWDUUAAAAAMGVds0xfyYQKLXRAsZKXdtM6g9w';

  static readonly DEV_BANK_PERSONALIZATION_JWT_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE3QCK3TadoqL2kHc7JPjVCNZmOopk
pOE27vJ8DEDrM2G+DN66dw1WjenpuKVGAQ5WjpA/Lx035xgga2I9w6+lQw==
-----END PUBLIC KEY-----`;
  static readonly PROD_BANK_PERSONALIZATION_JWT_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE/ryHYLYwXhMqNVxVGMeBBk6jwx97
GkfpxmaxTt3o43bRO0NCki/aDe/93Q5QLGiTs309kux6Rxf8//0lrt3Rkw==
-----END PUBLIC KEY-----`;

  static readonly DEV_TD_REFERRAL_ID = 'td-test';
  static readonly PROD_TD_REFERRAL_ID = 'tdbank';
  static readonly EXTERNAL_BANK_CONTACTS = [
    {
      devReferralId: 'td-test',
      prodReferralId: 'tdbank',
      contactUrl: 'https://www.forms.td.com/app/merchantsolutions/#/merchantsolutions/contact-form?page_code=merchantsolutionsoffer&marketing_code=None',
    },
    {
      devReferralId: 'transfund-test',
      prodReferralId: 'transfund',
      contactUrl: 'https://www.transfund.com/merchant-solutions/sales-Request',
    },
    {
      devReferralId: 'hwb-test',
      prodReferralId: 'hwb',
      contactUrl: 'https://www.hancockwhitney.com/merchant-services#get-started',
    },
  ];

  private cosApiClientInstance: AxiosInstance;
  private growthApiClientInstance: AxiosInstance;
  private dcsApiClientInstance: AxiosInstance;

  private context: Window;

  static getShopContentfulConfig(isPreview: boolean = Environment.usePreview()) {
    const CONTENTFUL_SPACE_ID = '5bhn3fhxj0f8';
    const CONTENTFUL_PROD_KEY = '28e3e17dccd1924baea17414ca70bbf08fd2f2401ad7b7d2aa41175e19407fd9';

    const CONTENTFUL_PREVIEW_KEY = '787e19c40a1ee5179fbefa89102fd0679ab08399efe00899f64f3449bf393b6d';

    return ContentfulClientConfig.create({
      space: CONTENTFUL_SPACE_ID,
      accessToken: isPreview ? CONTENTFUL_PREVIEW_KEY : CONTENTFUL_PROD_KEY,
      isPreview,
    });
  }

  static getCloverdotcomContentfulConfig(isPreview: boolean = Environment.usePreview(), testEnv = Environment.getTestEnvironment()) {
    const CONTENTFUL_SPACE_ID = 'v6ivjcl8qjz2';
    const CONTENTFUL_PROD_KEY = 'ee0c5eca89800229f98e2803d1553e30c8b45326f43f9948f4e0016defd39df0';

    const CONTENTFUL_PREVIEW_KEY = '4bd133f526e2c8909685943ecc59fb1513306ae883cc58ecd8bbeb9578f9342a';

    const config = testEnv
      ? {
        space: CONTENTFUL_SPACE_ID,
        accessToken: testEnv.accessToken,
        environment: testEnv.name,
        isPreview,
      }
      : {
        space: CONTENTFUL_SPACE_ID,
        accessToken: isPreview ? CONTENTFUL_PREVIEW_KEY : CONTENTFUL_PROD_KEY,
        isPreview,
      };

    return ContentfulClientConfig.create(config);
  }

  /**
   * GA Property ID
   */
  static getGAPropertyId(isProd: boolean = this.current.isCloverProdEnvironment) {
    return isProd ? this.PROD_GA_PROPERTY_ID : this.DEV_GA_PROPERTY_ID;
  }

  static isTdReseller(resellerSysId?: string, isProd: boolean = this.current.isCloverProdEnvironment) {
    return (resellerSysId && resellerSysId === (isProd ? this.PROD_TD_REFERRAL_ID : this.DEV_TD_REFERRAL_ID));
  }

  static getExternalBankContactLink(referralId?: string) {
    return this.EXTERNAL_BANK_CONTACTS
      .filter(item => referralId && referralId === (this.current.isCloverProdEnvironment ? item.prodReferralId : item.devReferralId))
      .map(item => item.contactUrl)[0];
  }

  static getPublicContentfulConfig(isPreview?: boolean) {
    const CONTENTFUL_SPACE_ID = 'hollw7yxz1ld';
    const CONTENTFUL_PROD_KEY = '80cf048775d8a68822b3110472e86f184411dacbf48cc960e210a0a4f5c4bbfd';

    const CONTENTFUL_PREVIEW_KEY = '671f8eddd6d0e746ea938c06fe38369bed16ac50d721272462980b882ccc1801';

    return ContentfulClientConfig.create({
      space: CONTENTFUL_SPACE_ID,
      accessToken: isPreview ? CONTENTFUL_PREVIEW_KEY : CONTENTFUL_PROD_KEY,
      isPreview,
    });
  }

  static useProd(): boolean {
    const obj = parse(location.search, { ignoreQueryPrefix: true });
    return obj.useProd === 'true';
  }

  static usePreview() {
    const useProd = Environment.useProd();
    const isDevEnv = this.current.isDev;

    if (!isDevEnv) return false;
    return !useProd;
  }

  static getTestEnvironment() {
    const params = parse(location.search, { ignoreQueryPrefix: true });
    const { accessToken, testEnvironment: name } = params;

    return accessToken && name && {
      accessToken: accessToken as string,
      name: name as string,
    };
  }

  constructor(context = window) {
    this.context = context;
  }
}

export const isDevEnvironment = Environment.current.isDev;

export const isExistingMerchant = Environment.current.isExistingMerchant;

export const bankPersonalizationJwtPublicKey = Environment.current.bankPersonalizationJwtPublicKey;
